/** @module Server-sent events read through fetch, so the token stays out of the URL. */
import { accessToken } from "@/lib/session";

export interface ServerEvent {
  event: string;
  data: unknown;
}

function parseBlock(block: string): ServerEvent | null {
  let name = "message";
  const payload: string[] = [];
  for (const line of block.split("\n")) {
    if (line.startsWith("event:")) name = line.slice(6).trim();
    else if (line.startsWith("data:")) payload.push(line.slice(5).trim());
  }
  if (payload.length === 0) return null;
  try {
    return { event: name, data: JSON.parse(payload.join("\n")) };
  } catch {
    return null;
  }
}

/**
 * Read one event stream until it ends or the signal aborts.
 *
 * EventSource cannot send an Authorization header, and a token in the query string
 * would end up in every access log.
 */
export async function streamEvents(
  url: string,
  onEvent: (event: ServerEvent) => void,
  signal: AbortSignal,
): Promise<void> {
  const token = accessToken();
  const response = await fetch(url, {
    headers: {
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    signal,
  });
  if (!response.ok || !response.body) return;

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let split = buffer.indexOf("\n\n");
    while (split !== -1) {
      const event = parseBlock(buffer.slice(0, split));
      buffer = buffer.slice(split + 2);
      if (event) onEvent(event);
      split = buffer.indexOf("\n\n");
    }
  }
  await reader.cancel().catch(() => undefined);
}
